import { DownloadSimpleIcon } from "@phosphor-icons/react/dist/csr/DownloadSimple";
import { Button, buttonVariants } from "@volca/ui/components/ui/button";
import { Card } from "@volca/ui/components/ui/card";
import { PageTitle } from "@volca/ui/components/ui/page-components";
import { Spinner } from "@volca/ui/components/ui/spinner";
import { Stack } from "@volca/ui/components/ui/stack";
import { Text } from "@volca/ui/components/ui/text";
import { cn } from "@volca/ui/lib/utils";
import type { PublicDocumentKind } from "./query";

export type PublicPdfState =
  | { status: "loading" }
  | { status: "available"; pdfUrl: string }
  | { status: "unavailable" }
  | { status: "error" };

interface PublicDocumentCardProps {
  kind: PublicDocumentKind;
  pdf: PublicPdfState;
  onSignIn: () => void;
}

export const PublicDocumentCard = ({
  kind,
  pdf,
  onSignIn,
}: PublicDocumentCardProps) => {
  if (pdf.status === "loading") {
    return (
      <Card className="w-full max-w-md p-6">
        <Stack gap={4} align="center" justify="center" className="py-6">
          <Spinner layout="centered" />
        </Stack>
      </Card>
    );
  }

  const available = pdf.status === "available";

  return (
    <Card className="w-full max-w-md p-6 pt-8">
      <Stack className="w-full" align="start" gap={6}>
        <Stack className="w-full" align="start" gap={2}>
          <PageTitle>
            {available ? `Vendor ${kind}` : `Sign in to view this ${kind}`}
          </PageTitle>
          <Text tone="muted">
            {pdf.status === "available" &&
              `Download the ${kind} PDF. You don't need a Volca login for it.`}
            {pdf.status === "unavailable" &&
              "Don't have a Volca login? Ask the person who sent this for a PDF link."}
            {pdf.status === "error" &&
              `Couldn't check for a downloadable PDF. Try again in a minute, or sign in to see the ${kind}.`}
          </Text>
        </Stack>
        <Stack className="w-full" align="start" gap={2}>
          {pdf.status === "available" && (
            <a
              href={pdf.pdfUrl}
              rel="noreferrer"
              className={cn(buttonVariants({ size: "lg" }), "w-full")}
            >
              <DownloadSimpleIcon aria-hidden />
              Download PDF
            </a>
          )}
          <Button
            className="w-full"
            size="lg"
            variant={available ? "secondary" : "default"}
            onClick={onSignIn}
          >
            Sign in to Volca
          </Button>
        </Stack>
      </Stack>
    </Card>
  );
};
